import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { ArrowLeft, Upload, Star, Trash2, Clock, ImagePlus, Plus, Sparkles, X } from "lucide-react";
import { adminApi } from "../api/adminApi";
import { getAdminToken } from "../../../helper/adminAuthStorage";
import RoomTypes from "./RoomTypes";
import RichText from "./RichText";
import HelpTip from "./HelpTip";
import "./hotelInventory.css";

const LocationSearch = ({ onSelect }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const tRef = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const runSearch = (text) => {
    clearTimeout(tRef.current);
    if (!text || text.trim().length < 3) { setResults([]); setOpen(false); setLoading(false); return; }
    setOpen(true);
    setLoading(true);
    tRef.current = setTimeout(async () => {
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=jsonv2&addressdetails=1&limit=6&q=${encodeURIComponent(text)}`;
        const res = await fetch(url, { headers: { Accept: "application/json" } });
        const data = await res.json();
        setResults(Array.isArray(data) ? data : []);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 500);
  };

  return (
    <div className="hi-fld" ref={boxRef} style={{ position: "relative" }}>
      <label>Search location</label>
      <input
        className="inp"
        value={q}
        placeholder="Search a place, landmark or address…"
        onChange={(e) => { setQ(e.target.value); runSearch(e.target.value); }}
        onFocus={() => { if (results.length) setOpen(true); }}
      />
      {open ? (
        <ul className="hi-loc-menu">
          {loading ? (
            <li className="hi-loc-empty">Searching…</li>
          ) : results.length ? (
            results.map((r) => (
              <li
                key={r.place_id}
                className="hi-loc-opt"
                onMouseDown={() => { onSelect(r); setQ(r.display_name); setResults([]); setOpen(false); }}
              >
                {r.display_name}
              </li>
            ))
          ) : (
            <li className="hi-loc-empty">No matches</li>
          )}
        </ul>
      ) : null}
    </div>
  );
};

const rsStyles = {
  control: (b, s) => ({
    ...b,
    minHeight: 44,
    borderRadius: 10,
    fontSize: 14,
    borderColor: s.isFocused ? "#3264FF" : "#e2e6ee",
    boxShadow: s.isFocused ? "0 0 0 3px rgba(50,100,255,.12)" : "none",
    ":hover": { borderColor: "#c7cfdd" },
  }),
  menu: (b) => ({ ...b, borderRadius: 10, overflow: "hidden", boxShadow: "0 10px 30px rgba(20,28,48,.14)" }),
  menuPortal: (b) => ({ ...b, zIndex: 9999 }),
  option: (b, s) => ({
    ...b,
    fontSize: 14,
    color: s.isSelected ? "#fff" : "#1b2439",
    backgroundColor: s.isSelected ? "#3264FF" : s.isFocused ? "#eef2ff" : "#fff",
  }),
};

const rsErrorStyles = {
  ...rsStyles,
  control: (b, s) => ({
    ...rsStyles.control(b, s),
    borderColor: "#e5484d",
    boxShadow: "0 0 0 3px rgba(229,72,77,.12)",
    ":hover": { borderColor: "#e5484d" },
  }),
};

const TP_HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const TP_MINUTES = ["00", "15", "30", "45"];

const TimePicker = ({ value, onChange, placeholder, error }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const hourRef = useRef(null);
  const valid = typeof value === "string" && /^\d{1,2}:\d{2}$/.test(value.trim());
  const [hh, mm] = valid ? value.trim().split(":") : ["", ""];

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    const sel = hourRef.current?.querySelector(".sel");
    if (sel) sel.scrollIntoView({ block: "center" });
    return () => { document.removeEventListener("mousedown", onDoc); document.removeEventListener("keydown", onKey); };
  }, [open]);

  const pick = (h, m) => onChange(`${h != null ? h : (hh || "00")}:${m != null ? m : (mm || "00")}`);

  return (
    <div className="hi-tp" ref={wrapRef}>
      <button type="button" className={`hi-tp-trigger${open ? " open" : ""}${error ? " err" : ""}`} onClick={() => setOpen((o) => !o)}>
        <span className={value ? "hi-tp-val" : "hi-tp-ph"}>{value || placeholder}</span>
        <Clock size={16} />
      </button>
      {open && (
        <div className="hi-tp-pop">
          <div className="hi-tp-cols">
            <div className="hi-tp-col">
              <div className="hi-tp-colh">Hour</div>
              <div className="hi-tp-list" ref={hourRef}>
                {TP_HOURS.map((h) => (
                  <button type="button" key={h} className={`hi-tp-opt${h === hh ? " is-sel" : ""}`} onClick={() => pick(h, null)}>{h}</button>
                ))}
              </div>
            </div>
            <div className="hi-tp-col">
              <div className="hi-tp-colh">Min</div>
              <div className="hi-tp-list">
                {TP_MINUTES.map((m) => (
                  <button type="button" key={m} className={`hi-tp-opt${m === mm ? " is-sel" : ""}`} onClick={() => pick(null, m)}>{m}</button>
                ))}
              </div>
            </div>
          </div>
          <div className="hi-tp-foot">
            <button type="button" className="hi-tp-clear" onClick={() => onChange("")}>Clear</button>
            <button type="button" className="hi-tp-done" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
};

const AMENITY_PRESETS = [
  { group: "Popular", items: ["Free WiFi", "Free parking", "Swimming pool", "Restaurant", "Room service", "Air conditioning"] },
  { group: "Wellness & Spa", items: ["Spa", "Sauna", "Steam room", "Gym / Fitness center", "Massage", "Jacuzzi"] },
  { group: "Food & Drink", items: ["Restaurant", "Bar", "Breakfast available", "Room service", "Café"] },
  { group: "Connectivity", items: ["Free WiFi", "Business center", "Meeting rooms"] },
  { group: "Services", items: ["24-hour front desk", "Concierge", "Airport shuttle", "Laundry service", "Currency exchange"] },
  { group: "Family", items: ["Family rooms", "Kids club", "Babysitting", "Children's playground"] },
  { group: "General", items: ["Elevator", "Non-smoking rooms", "Pet friendly", "Luggage storage", "Daily housekeeping"] },
];

const POLICY_FIELDS = [
  ["children", "Children"],
  ["extraBedsAndCribs", "Extra beds & cribs"],
  ["deposit", "Deposit"],
  ["pets", "Pets"],
  ["parking", "Parking"],
  ["mealsForPurchase", "Meals for purchase"],
  ["childrenMeals", "Children meals"],
  ["noShow", "No-show"],
  ["internet", "Internet"],
  ["additionalFees", "Additional fees"],
  ["identification", "Identification"],
  ["visaSupport", "Visa support"],
];

const emptyForm = () => ({
  name: "",
  propertyType: "",
  starRating: "",
  currency: "AED",
  description: "",
  location: { address: "", cityName: "", cityId: "", countryName: "", countryId: "", postalCode: "", lat: "", lng: "" },
  checkInTime: "",
  checkOutTime: "",
  frontDeskHours: "",
  contact: { phone: "", email: "", website: "" },
  languages: [],
  images: [],
  amenityGroups: [],
  policies: {},
  status: "draft",
});

const numOrUndef = (v) => (v === "" || v === null || v === undefined ? undefined : Number(v));

const HotelEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  const fileRef = useRef(null);
  const cityTimer = useRef(null);

  const [hotelId, setHotelId] = useState(isNew ? null : id);
  const [tab, setTab] = useState("profile");
  const [form, setForm] = useState(emptyForm());
  const [sd, setSd] = useState({ propertyTypes: [], currencies: [], languages: [], starRatings: [1, 2, 3, 4, 5] });
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const [triedSave, setTriedSave] = useState(false);

  const flash = (type, text) => setToast({ type, text });
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    (async () => {
      try {
        const res = await adminApi("GET", "/admin/inventory/static-data");
        const d = res?.data?.data || {};
        setSd({
          propertyTypes: d.propertyTypes || [],
          currencies: d.currencies || [],
          languages: d.languages || [],
          starRatings: d.starRatings || [1, 2, 3, 4, 5],
        });
      } catch {
        /* non-blocking */
      }
    })();
  }, []);

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const res = await adminApi("GET", `/admin/inventory/hotels/${id}`);
        const h = res?.data?.data?.hotel;
        if (h) setForm({ ...emptyForm(), ...h, location: { ...emptyForm().location, ...(h.location || {}) }, contact: { ...emptyForm().contact, ...(h.contact || {}) }, policies: h.policies || {} });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLoc = (k, v) => setForm((f) => ({ ...f, location: { ...f.location, [k]: v } }));

  const loadCities = (input) =>
    new Promise((resolve) => {
      if (cityTimer.current) clearTimeout(cityTimer.current);
      const s = String(input || "").trim();
      if (s.length < 2) {
        resolve([]);
        return;
      }
      cityTimer.current = setTimeout(async () => {
        try {
          const res = await adminApi("GET", `/admin/inventory/city-search?q=${encodeURIComponent(s)}`);
          const cities = res?.data?.data?.cities || [];
          resolve(cities.map((c) => ({ value: String(c.cityId), label: `${c.name}${c.countryName ? ", " + c.countryName : ""}`, city: c })));
        } catch {
          resolve([]);
        }
      }, 300);
    });

  const onCityChange = (opt) => {
    const c = opt?.city || {};
    setForm((f) => ({
      ...f,
      location: {
        ...f.location,
        cityId: opt ? c.cityId ?? "" : "",
        cityName: opt ? c.name ?? "" : "",
        countryName: opt ? c.countryName ?? "" : "",
        cityCode: opt ? c.code ?? "" : "",
        rateHawkCityCode: opt ? c.rateHawkCityCode ?? "" : "",
      },
    }));
  };
  const setContact = (k, v) => setForm((f) => ({ ...f, contact: { ...f.contact, [k]: v } }));
  const setPolicy = (k, v) => setForm((f) => ({ ...f, policies: { ...f.policies, [k]: v } }));

  const computeErrors = () => {
    const e = {};
    if (!form.name?.trim()) e.name = true;
    if (!form.propertyType) e.propertyType = true;
    if (!(Number(form.starRating) > 0)) e.starRating = true;
    if (!(form.location?.cityId || form.location?.cityName)) e.city = true;
    if (!String(form.location?.address || "").trim()) e.address = true;
    if (!String(form.location?.postalCode || "").trim()) e.postalCode = true;
    if (!/^\d{1,2}:\d{2}$/.test(String(form.checkInTime || "").trim())) e.checkInTime = true;
    if (!/^\d{1,2}:\d{2}$/.test(String(form.checkOutTime || "").trim())) e.checkOutTime = true;
    const email = String(form.contact?.email || "").trim();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) e.email = true;
    return e;
  };
  const errors = triedSave ? computeErrors() : {};

  const buildPayload = () => ({
    ...form,
    starRating: numOrUndef(form.starRating),
    location: {
      ...form.location,
      cityId: numOrUndef(form.location.cityId),
      countryId: numOrUndef(form.location.countryId),
      lat: numOrUndef(form.location.lat),
      lng: numOrUndef(form.location.lng),
    },
  });

  const save = async () => {
    const errs = computeErrors();
    if (Object.keys(errs).length) {
      setTriedSave(true);
      setTab("profile");
      flash("err", "Please fill all required fields");
      setTimeout(() => {
        const el = document.querySelector(".hi-section .inp.err, .hi-section .hi-tp-trigger.err, .hi-section .hi-city.err");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload();
      const res = hotelId
        ? await adminApi("POST", `/admin/inventory/hotels/${hotelId}`, payload)
        : await adminApi("POST", "/admin/inventory/hotels", payload);
      
      const code = res?.data?.statusCode ?? res?.status;
      if (!(code >= 200 && code < 300)) {
        throw new Error(res?.data?.message || "Save failed");
      }

      const saved = res?.data?.data?.hotel;
      if (!hotelId && saved?._id) {
        setHotelId(saved._id);
        navigate(`/admin/hotels/${saved._id}`, { replace: true });
      }
      if (saved) setForm((f) => ({ ...f, ...saved, location: { ...f.location, ...(saved.location || {}) }, contact: { ...f.contact, ...(saved.contact || {}) } }));
      flash("ok", "Saved");
    } catch (e) {
      flash("err", e?.response?.data?.message || e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (next) => {
    if (!hotelId) {
      flash("err", "Save the hotel first");
      return;
    }
    try {
      const res = await adminApi("POST", `/admin/inventory/hotels/${hotelId}/status`, { status: next });
      if (res?.data?.statusCode !== 200 && res?.status !== 200) {
        throw new Error(res?.data?.message || "Could not change status");
      }
      if (res?.data?.statusCode >= 400) {
        throw new Error(res?.data?.message || "Could not change status");
      }
      const saved = res?.data?.data?.hotel;
      set("status", saved?.status || next);
      flash("ok", `Status set to ${next}`);
    } catch (e) {
      flash("err", e?.response?.data?.message || e.message || "Could not change status");
    }
  };

  const onPickFiles = async (e) => {
    const files = e.target.files;
    if (!files?.length || !hotelId) return;
    setUploading(true);
    try {
      const fd = new FormData();
      Array.from(files).forEach((f) => fd.append("images", f));
      const resp = await fetch(`${process.env.client_apiEndpoint}/admin/inventory/hotels/${hotelId}/images`, {
        method: "POST",
        headers: { Authorization: getAdminToken() },
        body: fd,
      });
      if (!resp.ok) throw new Error("upload failed");
      const json = await resp.json();
      set("images", json?.data?.images || []);
      flash("ok", "Images uploaded");
    } catch {
      flash("err", "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const setCover = async (key) => {
    const images = (form.images || []).map((i) => ({ ...i, isCover: i.key === key }));
    set("images", images);
    try {
      await adminApi("POST", `/admin/inventory/hotels/${hotelId}`, { images });
      flash("ok", "Cover updated");
    } catch {
      flash("err", "Could not set cover");
    }
  };

  const deleteImage = async (key) => {
    try {
      const res = await adminApi("POST", `/admin/inventory/hotels/${hotelId}/images/delete`, { key });
      set("images", res?.data?.data?.images || []);
      flash("ok", "Image removed");
    } catch {
      flash("err", "Could not remove image");
    }
  };

  const scrollDown = () => setTimeout(() => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }), 100);
  const addGroup = () => { set("amenityGroups", [...(form.amenityGroups || []), { name: "", order: (form.amenityGroups || []).length, amenities: [] }]); scrollDown(); };
  const setGroup = (gi, patch) => set("amenityGroups", form.amenityGroups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const removeGroup = (gi) => set("amenityGroups", form.amenityGroups.filter((_, i) => i !== gi));
  const addAmenity = (gi) => { setGroup(gi, { amenities: [...(form.amenityGroups[gi].amenities || []), { name: "", highlight: false }] }); scrollDown(); };
  const setAmenity = (gi, ai, patch) => setGroup(gi, { amenities: form.amenityGroups[gi].amenities.map((a, i) => (i === ai ? { ...a, ...patch } : a)) });
  const removeAmenity = (gi, ai) => setGroup(gi, { amenities: form.amenityGroups[gi].amenities.filter((_, i) => i !== ai) });
  const addPreset = (p) => { set("amenityGroups", [...(form.amenityGroups || []), { name: p.group, order: (form.amenityGroups || []).length, amenities: p.items.map((name) => ({ name, highlight: false })) }]); scrollDown(); };

  if (loading) return <div className="gp"><div className="hi-empty">Loading…</div></div>;

  const langOptions = sd.languages.map((l) => ({ value: l, label: l }));

  return (
    <div className="gp">
      {toast ? <div className={`toast ${toast.type}`}>{toast.text}</div> : null}

      <div className="hi-pagehead">
        <div className="hi-pagehead-l">
          <button className="hi-iconbtn" onClick={() => navigate("/admin/hotels")} title="Back to hotels"><ArrowLeft size={18} /></button>
          <div style={{ minWidth: 0 }}>
            <div className="hi-pagehead-ttl">
              <h1>{form.name || (isNew ? "New hotel" : "Hotel")}</h1>
              <span className={`hi-badge ${form.status || "draft"}`}>{form.status || "draft"}</span>
              <HelpTip title="Editing a hotel">
                <ul>
                  <li><strong>Profile</strong> — name, star rating, city &amp; map location, contact and check-in times. Save once to unlock the other tabs.</li>
                  <li><strong>Photos</strong> — drag in images; the first is the cover shown in search.</li>
                  <li><strong>Amenities</strong> — tick facilities; use the quick-add groups to fill common ones fast.</li>
                  <li><strong>Policies</strong> — check-in/out, children, deposit, etc. shown on the booking page.</li>
                  <li><strong>Rooms</strong> — add room types, then their rate plans, prices &amp; availability.</li>
                </ul>
                <p><strong>Publish</strong> goes live in B2B search — it needs at least one room with a price and open availability.</p>
              </HelpTip>
            </div>
            {form.code ? <div className="hi-pagehead-code">{form.code}</div> : null}
          </div>
        </div>
        <div className="hi-toolbar">
          {form.status !== "published" ? (
            <button className="hi-btn soft" onClick={() => changeStatus("published")} disabled={!hotelId}>Publish</button>
          ) : (
            <button className="hi-btn soft" onClick={() => changeStatus("hidden")}>Unpublish</button>
          )}
          <button className="hi-btn primary" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="hi-tabs">
        {["profile", "photos", "amenities", "policies", "rooms"].map((t) => (
          <button
            key={t}
            className={`hi-tab${tab === t ? " active" : ""}`}
            onClick={() => setTab(t)}
            disabled={t !== "profile" && !hotelId}
            style={{ textTransform: "capitalize" }}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === "profile" && (
        <div className="hi-section">
          <div className="hi-grid">
            <div className="hi-fld">
              <label>Hotel name *</label>
              <input className={`inp${errors.name ? " err" : ""}`} value={form.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div className="hi-fld">
              <label>Property type *</label>
              <select className={`inp${errors.propertyType ? " err" : ""}`} value={form.propertyType} onChange={(e) => set("propertyType", e.target.value)}>
                <option value="">Select…</option>
                {sd.propertyTypes.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div className="hi-fld">
              <label>Star rating *</label>
              <select className={`inp${errors.starRating ? " err" : ""}`} value={form.starRating} onChange={(e) => set("starRating", e.target.value)}>
                <option value="">Select…</option>
                {sd.starRatings.map((s) => <option key={s} value={s}>{s} star</option>)}
              </select>
            </div>
            <div className="hi-fld">
              <label>Currency</label>
              <select className="inp" value={form.currency} onChange={(e) => set("currency", e.target.value)}>
                {sd.currencies.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <div className="hi-grid one" style={{ marginTop: 16 }}>
            <div className="hi-fld">
              <label>Description</label>
              <textarea className="inp" value={form.description} onChange={(e) => set("description", e.target.value)} />
            </div>
            <div className="hi-fld">
              <label>Languages spoken</label>
              <Select
                isMulti
                options={langOptions}
                value={(form.languages || []).map((l) => ({ value: l, label: l }))}
                onChange={(vals) => set("languages", (vals || []).map((v) => v.value))}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={rsStyles}
              />
            </div>
          </div>

          <h3 style={{ marginTop: 20 }}>Location</h3>
          <div className="hi-grid one">
            <div className={`hi-fld hi-city${errors.city ? " err" : ""}`}>
              <label>City * <span style={{ color: "#98a2b6", fontWeight: 400 }}>(search &amp; select — sets city id and country automatically)</span></label>
              <AsyncSelect
                cacheOptions
                loadOptions={loadCities}
                onChange={onCityChange}
                isClearable
                placeholder="Type a city name…"
                value={form.location.cityName ? { value: String(form.location.cityId || ""), label: `${form.location.cityName}${form.location.countryName ? ", " + form.location.countryName : ""}` } : null}
                noOptionsMessage={({ inputValue }) => (inputValue && inputValue.length >= 2 ? "No cities found" : "Type at least 2 letters")}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                styles={errors.city ? rsErrorStyles : rsStyles}
              />
            </div>
          </div>
          <div className="hi-grid one">
            <LocationSearch
              onSelect={(r) => {
                if (r?.lat) setLoc("lat", r.lat);
                if (r?.lon) setLoc("lng", r.lon);
                if (r?.display_name) setLoc("address", r.display_name);
                if (r?.address?.postcode) setLoc("postalCode", r.address.postcode);
              }}
            />
          </div>
          <div className="hi-grid">
            <div className="hi-fld"><label>Country</label><input className="inp" value={form.location.countryName || ""} readOnly placeholder="Set from selected city" /></div>
            <div className="hi-fld"><label>Address *</label><input className={`inp${errors.address ? " err" : ""}`} value={form.location.address} onChange={(e) => setLoc("address", e.target.value)} /></div>
            <div className="hi-fld"><label>Postal code *</label><input className={`inp${errors.postalCode ? " err" : ""}`} value={form.location.postalCode} onChange={(e) => setLoc("postalCode", e.target.value)} /></div>
            <div className="hi-fld"><label>Latitude</label><input className="inp" value={form.location.lat} onChange={(e) => setLoc("lat", e.target.value)} /></div>
            <div className="hi-fld"><label>Longitude</label><input className="inp" value={form.location.lng} onChange={(e) => setLoc("lng", e.target.value)} /></div>
          </div>

          <h3 style={{ marginTop: 20 }}>Timings & contact</h3>
          <div className="hi-grid">
            <div className="hi-fld"><label>Check-in time *</label><TimePicker value={form.checkInTime} placeholder="14:00" error={errors.checkInTime} onChange={(v) => set("checkInTime", v)} /></div>
            <div className="hi-fld"><label>Check-out time *</label><TimePicker value={form.checkOutTime} placeholder="12:00" error={errors.checkOutTime} onChange={(v) => set("checkOutTime", v)} /></div>
            <div className="hi-fld"><label>Front desk hours</label><input className="inp" placeholder="24 hours" value={form.frontDeskHours} onChange={(e) => set("frontDeskHours", e.target.value)} /></div>
            <div className="hi-fld"><label>Phone</label><input className="inp" value={form.contact.phone} onChange={(e) => setContact("phone", e.target.value)} /></div>
            <div className="hi-fld"><label>Email * <span style={{ color: "#98a2b6", fontWeight: 400 }}>(booking confirmations)</span></label><input className={`inp${errors.email ? " err" : ""}`} value={form.contact.email} onChange={(e) => setContact("email", e.target.value)} /></div>
            <div className="hi-fld"><label>Website</label><input className="inp" value={form.contact.website} onChange={(e) => setContact("website", e.target.value)} /></div>
          </div>
        </div>
      )}

      {tab === "photos" && (
        <div className="hi-section">
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={onPickFiles} />
          {(form.images || []).length === 0 ? (
            <button type="button" className="hi-dropzone" onClick={() => fileRef.current?.click()} disabled={uploading}>
              <span className="hi-dropzone-ic"><ImagePlus size={26} /></span>
              <span className="hi-dropzone-ttl">{uploading ? "Uploading…" : "Upload hotel photos"}</span>
              <span className="hi-dropzone-sub">PNG or JPG. The first image, or the one you star, becomes the cover.</span>
              <span className="hi-btn primary hi-dropzone-btn"><Upload size={16} />Choose images</span>
            </button>
          ) : (
            <>
              <div className="hi-photohead">
                <div>
                  <div className="hi-photohead-ttl">{form.images.length} photo{form.images.length > 1 ? "s" : ""}</div>
                  <div className="hi-sub">Click the star to set the cover photo.</div>
                </div>
                <button className="hi-btn primary" onClick={() => fileRef.current?.click()} disabled={uploading}>
                  <Upload size={16} />{uploading ? "Uploading…" : "Add more"}
                </button>
              </div>
              <div className="hi-gallery">
                {form.images.map((img) => (
                  <div className="hi-imgcard" key={img.key}>
                    <img src={img.url} alt="" />
                    <div className="bar">
                      {img.isCover ? <span className="cover">Cover</span> : <button className="hi-mini set" onClick={() => setCover(img.key)}><Star size={13} /> Cover</button>}
                      <button className="hi-mini del" onClick={() => deleteImage(img.key)}><Trash2 size={13} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {tab === "amenities" && (
        <div className="hi-section">
          <div className="hi-photohead">
            <div>
              <div className="hi-photohead-ttl">Amenities</div>
              <div className="hi-sub">Group amenities (e.g. “Wellness”, “Connectivity”) </div>
            </div>
            <button className="hi-btn soft" onClick={addGroup}><Plus size={16} />Add blank group</button>
          </div>

          <div className="hi-preset">
            <span className="hi-preset-lbl">Quick add a group</span>
            <div className="hi-preset-chips">
              {AMENITY_PRESETS.map((p) => (
                <button type="button" key={p.group} className="hi-chip" onClick={() => addPreset(p)}>
                  <Plus size={13} />{p.group}
                </button>
              ))}
            </div>
          </div>

          {(form.amenityGroups || []).length === 0 ? (
            <div className="hi-emptycard">
              <span className="hi-emptycard-ic"><Sparkles size={24} /></span>
              <div className="hi-emptycard-ttl">No amenities yet</div>
              <div className="hi-emptycard-sub">Pick a quick-add group above to start with common amenities you can edit, or add a blank group and fill it in yourself.</div>
            </div>
          ) : (
            form.amenityGroups.map((g, gi) => (
              <div className="hi-amgroup" key={gi}>
                <div className="hi-amgroup-head">
                  <div className="hi-fld hi-amgroup-name">
                    <label>Group name</label>
                    <input className="inp" placeholder="e.g. Wellness" value={g.name} onChange={(e) => setGroup(gi, { name: e.target.value })} />
                  </div>
                  <button className="hi-btn ghost danger" onClick={() => removeGroup(gi)}><Trash2 size={15} />Remove</button>
                </div>
                <div className="hi-amlist">
                  {(g.amenities || []).length === 0 ? (
                    <div className="hi-amempty">No amenities in this group yet — add your first below.</div>
                  ) : (
                    (g.amenities || []).map((a, ai) => (
                      <div className="hi-amrow" key={ai} style={{ position: "relative" }}>
                        <input className="inp" style={{ paddingRight: 32 }} placeholder="Amenity name (e.g. Free WiFi)" value={a.name} onChange={(e) => setAmenity(gi, ai, { name: e.target.value })} />
                        <button type="button" onClick={() => removeAmenity(gi, ai)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", cursor: "pointer", padding: 4, display: "flex" }} title="Remove amenity">
                          <X size={16} color="#ef4444" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <button type="button" className="hi-addrow" onClick={() => addAmenity(gi)}><Plus size={15} />Add amenity</button>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "policies" && (
        <div className="hi-section">
          <div className="hi-photohead">
            <div>
              <div className="hi-photohead-ttl">Property policies</div>
              <div className="hi-sub">Use bold, lists and links to format each policy — this renders on the hotel details page.</div>
            </div>
          </div>
          <div className="hi-grid two">
            {POLICY_FIELDS.map(([k, label]) => (
              <div className="hi-fld" key={k}>
                <label>{label}</label>
                <RichText value={form.policies?.[k] || ""} onChange={(html) => setPolicy(k, html)} placeholder={`Describe the ${label.toLowerCase()} policy…`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "rooms" && hotelId && <div className="hi-section"><RoomTypes hotelId={hotelId} /></div>}
    </div>
  );
};

export default HotelEditor;
